import { useEffect } from "react";
import type { AppStores } from "./app-context";
import { detectClientDevice, primaryScreenForDevice } from "./client-device";

const RESIZE_DEBOUNCE_MS = 200;

/**
 * Keeps device/screen classification live across a browser resize or
 * maximize that happens after initial mount. Every `ViewDeviceType` consumer
 * (`SessionStore.clientDevice`, `FeatureRegistryStore`'s device/screen gating,
 * capability discovery) previously froze whatever `window.innerWidth` was at
 * the moment the app first mounted — resizing the browser afterward left the
 * shell nav, feature gating and discovered capabilities stuck on the wrong
 * classification until a full page reload.
 *
 * This value also drives `operations-route.tsx`'s scoped desktop/tablet/mobile
 * workspace selection. Each device class has its own persistence key, and the
 * outgoing FlexLayout model flushes before unmount, so reclassification never
 * reuses an incompatible model or discards an authorized arrangement.
 */
export function useResponsiveDevice(stores: AppStores): void {
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    function applyIfChanged(): void {
      const device = detectClientDevice();
      if (device === stores.session.clientDevice) return;
      const screen = primaryScreenForDevice(device);
      stores.session.setClientDevice(device);
      stores.features.setDeviceAndScreen(device, screen);
      if (
        stores.session.status === "authenticated" &&
        stores.session.permissionDiscovery === "ready" &&
        stores.session.activeOrganizationId !== null
      ) {
        void stores.session.refreshCapabilities(stores.api);
      }
    }

    function handleResize(): void {
      if (timeout !== undefined) clearTimeout(timeout);
      timeout = setTimeout(applyIfChanged, RESIZE_DEBOUNCE_MS);
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (timeout !== undefined) clearTimeout(timeout);
    };
  }, [stores]);
}
